import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from 'urql';
import {
  AutomationForm,
  FormValues,
} from '~/components/automation/AutomationForm/AutomationForm';
import {
  getTemplateBehavior,
  isAssetDrivenTemplate,
} from '~/components/job/AssetDrivenTemplateIndicator';
import { Dialog } from '~/components/ui/Dialog';
import { getFragmentData, graphql } from '~/gql';
import { AutomationInput } from '~/gql/graphql';
import { JobTemplateFields } from '~/graphql/fragment/JobTemplateFields';
import { UpdateJobTemplateDocument } from '~/graphql/mutations/job-template/UpdateJobTemplate';
import {
  handleTemplateAction,
  isTemplateModified,
} from '~/helpers/automationTemplateHelpers';

export const AutomationFormDocument = graphql(`
  query AutomationForm($id: ID) {
    contacts(status: Active) {
      ...ContactFields
    }
    jobTemplates(status: Active) {
      ...JobTemplateFields
    }
    automation(id: $id) {
      id
      status
      name
      on
      whereType
      matchAttribute {
        key: name
        value
      }
      sites {
        id
        name
        image
      }
      jobName
      template {
        id
        status
        name
        notes
        tags {
          id
          entityType
          category
          name
          description
          colour
        }
        includeAttributes
        jobAttributeIds
        attachments {
          id
          kind
          ext
          thumb
          url
          originalFilename
        }
        tasks {
          id
          type
          name
          description
          attributes {
            id
            type
            category
            name
            value
          }
          jobAttributeIds
          attribute {
            id
            attributes
          }
          purchase {
            id
            spaces
          }
          stocktake {
            id
            include
            itemStatus
            spaces
            items {
              id
              adjustment
              newOnHand
            }
          }
          config {
            selection
            spaces
            assetTypes
            allow
          }
          assets {
            id
            name
            type
          }
        }
      }
      owners {
        id
      }
      assignees {
        id
      }
      day
      time
      repeatStartDate
      repeat {
        freq
        interval
        byday
        bymonthday
      }
      maintainRepeats
    }
  }
`);

const CreateAutomationDocument = graphql(`
  mutation CreateAutomation($input: AutomationInput!) {
    createAutomation(input: $input)
  }
`);

const CreateJobTemplateDocument = graphql(`
  mutation CreateJobTemplate($input: JobTemplateInput!) {
    createJobTemplate(input: $input) {
      id
    }
  }
`);

// (freq already accepted as strings by backend in this flow)

// Reverse normalization: Convert UI day names to two-letter weekday codes
// Backend RRule builder expects lowercase 2-letter codes that it uppercases internally
// before passing to Weekday.fromStr (which only accepts uppercase: SU, MO, TU, WE, TH, FR, SA)
// Frontend sends: 'mo','tu','we','th','fr','sa','su'
// Backend converts to: 'MO','TU','WE','TH','FR','SA','SU'
const denormalizeByDay = (
  byday: string[] | null | undefined
): string[] | undefined => {
  if (!byday || byday.length === 0) return undefined;

  const fullNameToTwoLetter: Record<string, string> = {
    sunday: 'su',
    monday: 'mo',
    tuesday: 'tu',
    wednesday: 'we',
    thursday: 'th',
    friday: 'fr',
    saturday: 'sa',
  };
  const threeToTwo: Record<string, string> = {
    sun: 'su',
    mon: 'mo',
    tue: 'tu',
    wed: 'we',
    thu: 'th',
    fri: 'fr',
    sat: 'sa',
  };

  const denormalized = byday.map((day) => {
    const lc = day.trim().toLowerCase();
    if (fullNameToTwoLetter[lc]) {
      const code = fullNameToTwoLetter[lc];
      return code;
    }
    if (lc.length === 3 && threeToTwo[lc]) {
      const code = threeToTwo[lc];

      return code;
    }
    if (lc.length === 2) return lc; // already a 2-letter code

    console.warn(
      `[formToInput] ⚠️ Unknown day value: "${day}", defaulting to 'su'`
    );
    return 'su';
  });

  return denormalized;
};

export function formToInput(data: FormValues): AutomationInput {
  // Repeat transformation logic (force overwrite of incompatible fields)
  let repeatByMonthDay: string | undefined = undefined;
  let repeatByDay: string[] | undefined = undefined;

  if (data.repeat.freq === 'daily') {
    // Clear any previous weekly/monthly parts
    repeatByDay = undefined;
    repeatByMonthDay = undefined;
  } else if (data.repeat.freq === 'weekly') {
    repeatByDay = denormalizeByDay(data.repeat.byday);
    repeatByMonthDay = undefined;
  } else if (data.repeat.freq === 'monthly') {
    if (data.repeat.bymonthday?.startsWith('bymonthday:')) {
      repeatByMonthDay = data.repeat.bymonthday.substring('bymonthday:'.length);
      repeatByDay = undefined;
    } else if (data.repeat.bymonthday?.startsWith('byday:')) {
      const raw = data.repeat.bymonthday.substring('byday:'.length); // e.g. "5,Thursday" or "2,Monday"
      const [nStr, dayName] = raw.split(',');
      const n = parseInt(nStr, 10);
      const isLast = n === 5;
      const nOut = isLast ? -1 : n;

      // Map full day name to 2-letter code
      const dayNameLower = (dayName || '').trim().toLowerCase();
      const dayMap: Record<string, string> = {
        sunday: 'su',
        monday: 'mo',
        tuesday: 'tu',
        wednesday: 'we',
        thursday: 'th',
        friday: 'fr',
        saturday: 'sa',
      };
      const dayCode = dayMap[dayNameLower] || 'su';

      repeatByDay = [`${nOut}${dayCode}`];
      repeatByMonthDay = undefined;
    } else {
      // If nothing selected, default to the calendar day-of-month
      repeatByMonthDay = undefined;
      repeatByDay = undefined;
    }
  }

  // Use signed 'day' directly from the form (negative=before, positive=after, 0=same day)
  const day = typeof data.day === 'number' ? data.day : 0;

  // Only include fields defined in AutomationInput type
  const converted: AutomationInput = {
    // Core fields
    name: data.name,
    status: data.status,
    on: data.on,
    whereType: data.whereType,

    // Matching & sites
    matchAttribute: data.matchAttribute.map(({ key, value }) => ({
      name: key,
      value,
    })),
    sites: data.sites.map(({ id }) => id),

    // Job configuration
    jobName: data.jobName,
    template: data.template,
    owner: data.owner,
    assignee: data.assignee,

    // Schedule fields
    day,
    time: data.time,
    repeatStartDate: data.repeatStartDate,

    // Repeat configuration
    repeat: {
      freq: data.repeat.freq, // 'daily' | 'weekly' | 'monthly'
      interval: String(data.repeat.interval || '1'), // GraphQL expects string
      // Always send arrays when we want to clear stale server values
      bymonthday: repeatByMonthDay,
      byday: repeatByDay,
    },

    // Runtime options
    runOnExistingBookings: data.runOnExistingBookings === 'true',
    maintainRepeats:
      data.maintainRepeats !== undefined && data.maintainRepeats !== null
        ? data.maintainRepeats
        : 1,
  };

  // Validate output - throw errors if data is lost during conversion
  if (data.sites.length > 0) {
    if (!Array.isArray(converted.sites) || converted.sites.length === 0) {
      throw new Error(
        `Validation failed: sites were lost during conversion. Input had ${data.sites.length} site(s) but output is empty.`
      );
    }
    if (converted.sites.length !== data.sites.length) {
      throw new Error(
        `Validation failed: sites count mismatch. Input had ${data.sites.length} site(s) but output has ${converted.sites.length}.`
      );
    }
  }
  if (data.owner && data.owner.length > 0) {
    if (
      !converted.owner ||
      !Array.isArray(converted.owner) ||
      converted.owner.length === 0
    ) {
      throw new Error(
        `Validation failed: owners were lost during conversion. Input had ${data.owner.length} owner(s) but output is empty or undefined.`
      );
    }
  }
  if (data.assignee && data.assignee.length > 0) {
    if (
      !converted.assignee ||
      !Array.isArray(converted.assignee) ||
      converted.assignee.length === 0
    ) {
      throw new Error(
        `Validation failed: assignees were lost during conversion. Input had ${data.assignee.length} assignee(s) but output is empty or undefined.`
      );
    }
  }

  return converted;
}

export function Component() {
  const navigate = useNavigate();
  const params = useParams();

  const [, createAutomation] = useMutation(CreateAutomationDocument);
  const [, updateJobTemplate] = useMutation(UpdateJobTemplateDocument);
  const [, createJobTemplate] = useMutation(CreateJobTemplateDocument);
  const [result] = useQuery({
    query: AutomationFormDocument,
    variables: { id: params.automationId },
    requestPolicy: 'network-only',
  });

  const [showTemplateActionDialog, setShowTemplateActionDialog] =
    useState(false);
  const [pendingFormData, setPendingFormData] = useState<FormValues | null>(
    null
  );

  // Memoize templates mapping to avoid remapping on every action and prevent stale closures
  const templates = useMemo(() => {
    if (!result.data?.jobTemplates) return [];
    return result.data.jobTemplates.map((template) =>
      getFragmentData(JobTemplateFields, template)
    );
  }, [result.data?.jobTemplates]);

  // Check if the selected template is asset-driven
  // IMPORTANT: These hooks must be called before any early returns to avoid hook count mismatch
  const selectedTemplate = useMemo(() => {
    if (!pendingFormData?.template) return null;
    return templates.find((t) => t.id === pendingFormData.template);
  }, [pendingFormData?.template, templates]);

  const isAssetDriven = useMemo(() => {
    if (!selectedTemplate) return false;
    return isAssetDrivenTemplate(getTemplateBehavior(selectedTemplate));
  }, [selectedTemplate]);

  if (!result.data) {
    return null;
  }

  const handleTemplateActionWrapper = async (
    action: 'update' | 'create',
    formData: FormValues
  ): Promise<FormValues | null> => {
    if (!result.data) return null;

    const actionResult = await handleTemplateAction({
      action,
      formData,
      jobTemplates: templates,
      updateJobTemplate,
      createJobTemplate,
    });

    // Return formData only on success, null on failure
    return actionResult.success ? actionResult.formData : null;
  };

  const handleSubmitWithTemplateCheck = async (data: FormValues) => {
    if (!result.data) return;

    // Check if template was modified
    if (data.templateData && data.template) {
      const selectedTemplate = templates.find((t) => t.id === data.template);

      if (selectedTemplate && isTemplateModified(data, selectedTemplate)) {
        // Template was modified, show dialog
        setPendingFormData(data);
        setShowTemplateActionDialog(true);
        return; // Don't submit yet, wait for user choice
      }
    }

    // No template changes, proceed normally
    const convertedInput = formToInput(data);
    const res = await createAutomation({ input: convertedInput });

    if (res.error) {
      toast.error(
        `Failed to create automation: ${res.error.message || 'Unknown error'}`
      );
      return;
    }

    const id = res.data?.createAutomation;
    if (id) {
      return navigate(`/automations/${id}`);
    }
  };

  return (
    <>
      {/* Template Action Dialog */}
      <Dialog
        show={showTemplateActionDialog}
        onClose={() => {
          setShowTemplateActionDialog(false);
          setPendingFormData(null);
        }}
        title={
          isAssetDriven
            ? 'Save Asset-Driven Template Changes'
            : 'Template Changes Detected'
        }
      >
        <div className='p-4'>
          {isAssetDriven ? (
            <>
              <p className='mb-4 text-sm text-grey-50'>
                Asset-driven templates cannot be modified. We will create a new
                Job template with your current selection.
              </p>
              <div className='m-4 text-right'>
                <button
                  type='button'
                  onClick={() => {
                    setShowTemplateActionDialog(false);
                    setPendingFormData(null);
                  }}
                  className='text-xs uppercase text-grey-50 transition-all duration-300 hover:text-grey-70'
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={async () => {
                    if (pendingFormData) {
                      const updatedData = await handleTemplateActionWrapper(
                        'create',
                        pendingFormData
                      );

                      // If template action failed, abort automation creation
                      // Clear pendingFormData and close dialog on failure
                      if (!updatedData) {
                        setPendingFormData(null);
                        setShowTemplateActionDialog(false);
                        return;
                      }

                      // Clear pendingFormData and close dialog on success
                      setPendingFormData(null);
                      setShowTemplateActionDialog(false);
                      // Continue with automation creation
                      const convertedInput = formToInput(updatedData);
                      const res = await createAutomation({
                        input: convertedInput,
                      });

                      if (res.error) {
                        toast.error(
                          `Failed to create automation: ${
                            res.error.message || 'Unknown error'
                          }`
                        );
                        return;
                      }

                      const id = res.data?.createAutomation;
                      if (id) {
                        navigate(`/automations/${id}`);
                      }
                    }
                  }}
                  className='ml-8 rounded-md bg-brand px-4 py-2 text-xs uppercase text-white shadow transition-all duration-300 hover:bg-brand-dark'
                >
                  OK
                </button>
              </div>
            </>
          ) : (
            <>
              <p className='mb-4 text-sm text-grey-50'>
                You&apos;ve made changes to the template. How would you like to
                save these changes?
              </p>

              <div className='space-y-3'>
                <button
                  type='button'
                  onClick={async () => {
                    if (pendingFormData) {
                      const updatedData = await handleTemplateActionWrapper(
                        'update',
                        pendingFormData
                      );

                      // If template action failed, abort automation creation
                      // Clear pendingFormData and close dialog on failure
                      if (!updatedData) {
                        setPendingFormData(null);
                        setShowTemplateActionDialog(false);
                        return;
                      }

                      // Clear pendingFormData and close dialog on success
                      setPendingFormData(null);
                      setShowTemplateActionDialog(false);
                      // Continue with automation creation
                      const convertedInput = formToInput(updatedData);
                      const res = await createAutomation({
                        input: convertedInput,
                      });

                      if (res.error) {
                        toast.error(
                          `Failed to create automation: ${
                            res.error.message || 'Unknown error'
                          }`
                        );
                        return;
                      }

                      const id = res.data?.createAutomation;
                      if (id) {
                        navigate(`/automations/${id}`);
                      }
                    }
                  }}
                  className='w-full rounded border border-grey-20 bg-white p-4 pt-3 text-left hover:bg-grey-5'
                >
                  <div className='font-medium'>Update existing template</div>
                  <div className='mt-1 text-xs text-secondary'>
                    Changes will be saved to the current template. This affects
                    all automations using this template.
                  </div>
                </button>

                <button
                  type='button'
                  onClick={async () => {
                    if (pendingFormData) {
                      const updatedData = await handleTemplateActionWrapper(
                        'create',
                        pendingFormData
                      );

                      // If template action failed, abort automation creation
                      // Clear pendingFormData and close dialog on failure
                      if (!updatedData) {
                        setPendingFormData(null);
                        setShowTemplateActionDialog(false);
                        return;
                      }

                      // Clear pendingFormData and close dialog on success
                      setPendingFormData(null);
                      setShowTemplateActionDialog(false);
                      // Continue with automation creation
                      const convertedInput = formToInput(updatedData);
                      const res = await createAutomation({
                        input: convertedInput,
                      });

                      if (res.error) {
                        toast.error(
                          `Failed to create automation: ${
                            res.error.message || 'Unknown error'
                          }`
                        );
                        return;
                      }

                      const id = res.data?.createAutomation;
                      if (id) {
                        navigate(`/automations/${id}`);
                      }
                    }
                  }}
                  className='w-full rounded border border-grey-20 bg-white p-4 pt-3 text-left hover:bg-grey-5'
                >
                  <div className='font-medium'>Create new template</div>
                  <div className='mt-1 text-xs text-secondary'>
                    A new template will be created with your changes. The
                    original template remains unchanged.
                  </div>
                </button>
              </div>
            </>
          )}
        </div>
      </Dialog>

      <AutomationForm
        data={result.data}
        onSubmit={handleSubmitWithTemplateCheck}
      />
    </>
  );
}
